import { Button, Tooltip } from 'antd'
import { DescribedTerm } from 'features/libra/components/DescribedTerm'
import { getFieldSource } from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValueSource } from 'features/libra/ontology/ontologyFieldTypes'
import { ReadOnlySourceDisplay } from 'features/libra/stepComponents/conditionalForm/conditionalFormSection/SourceInput'
import { FC, ReactNode } from 'react'
import styled from 'styled-components'

export type InputProvenance =
  | { kind: 'fieldValue'; source: FieldValueSource }
  | {
      kind: 'assetModel'
      field: string
      /**
       * Optional callback fired when the engineer clicks the "Edit Asset
       * Model" affordance. Recommended host: an in-place drawer/modal that
       * mounts `EditGeckoRichAssetModel`, so the engineer doesn't lose the
       * surrounding step context. If omitted, the row just shows the field
       * label as a passive note.
       */
      onEdit?: () => void
      /** Label override for the action button. Defaults to "Edit Asset Model". */
      editLabel?: string
      /**
       * Optional citation for the asset-model value, resolved from the ticket's
       * `inputProvenance` overlay (keyed by `assetModelProvenanceKey`). The
       * value itself lives on the snapshot; this only records where it came
       * from. When present, renders the read-only `ReadOnlySourceDisplay` chip
       * (and doc-viewer jump). Editing sources happens in the Asset Model step's
       * editor, not here.
       */
      source?: FieldValueSource | null
    }
  | { kind: 'constant'; note: string }

interface InputProvenanceRowProps {
  /** Symbol JSX (use `<sub>` / `<sup>` for math). */
  symbol: ReactNode
  /** Pre-formatted value with unit (e.g. "3.0 MPa"). */
  value: string
  /** Where the value came from. Determines which source affordance renders. */
  provenance: InputProvenance
  /**
   * Optional plain-language description of what the symbol represents. When
   * present, the symbol gets a dotted underline and shows this on hover.
   */
  description?: ReactNode
}

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.25rem 0;
`

const Symbol = styled.span`
  min-width: 4rem;
  font-family:
    ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
`

const Value = styled.span`
  min-width: 7rem;
  font-family:
    ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const SourceLabel = styled.span`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.75rem;
`

const SourceChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const StepNavLink = styled(Button).attrs({ type: 'link', size: 'small' })`
  padding: 0 0.25rem;
  height: auto;
  font-size: 0.75rem;
`

/**
 * One row in the Inputs block of a calculation step. Renders
 * `[symbol] = [value]  [source affordance]`. The source affordance is the
 * engineer's path back to the value's origin:
 *
 * - **fieldValue**: `ReadOnlySourceDisplay` from the citation infrastructure --
 *   shows the cited document and lets the engineer click to jump the workspace
 *   document viewer to the cited page (via `useLibraDocument`).
 * - **assetModel**: a link button that fires the caller's `onEdit` callback.
 *   Caller decides what happens (recommended: open an in-place drawer/modal
 *   mounting `EditGeckoRichAssetModel` so the engineer stays in the current
 *   step). If `onEdit` is omitted, the row just shows the field label
 *   as a passive note. If a `source` citation is supplied (from the ticket's
 *   `inputProvenance` overlay), it also renders the doc-viewer citation chip.
 * - **constant**: a muted note explaining the source (engineering judgment /
 *   hardcoded default).
 */
export const InputProvenanceRow: FC<InputProvenanceRowProps> = ({
  symbol,
  value,
  provenance,
  description,
}) => {
  let sourceContent: ReactNode = null
  switch (provenance.kind) {
    case 'fieldValue': {
      sourceContent = (
        <SourceChip>
          <SourceLabel>from Data Gathering</SourceLabel>
          {provenance.source ? (
            <ReadOnlySourceDisplay source={provenance.source} />
          ) : (
            <SourceLabel>(no citation)</SourceLabel>
          )}
        </SourceChip>
      )
      break
    }
    case 'assetModel': {
      sourceContent = (
        <SourceChip>
          <SourceLabel>from Asset Model</SourceLabel>
          <Tooltip
            title={`${provenance.field} on the component's asset-model entry`}>
            {provenance.onEdit ? (
              <StepNavLink onClick={provenance.onEdit}>
                {provenance.editLabel ?? 'Edit Asset Model'}
              </StepNavLink>
            ) : (
              <SourceLabel>({provenance.field})</SourceLabel>
            )}
          </Tooltip>
          {provenance.source ? (
            <ReadOnlySourceDisplay source={provenance.source} />
          ) : null}
        </SourceChip>
      )
      break
    }
    case 'constant': {
      sourceContent = (
        <SourceChip>
          <SourceLabel>{provenance.note}</SourceLabel>
        </SourceChip>
      )
      break
    }
  }

  return (
    <Row>
      <Symbol>
        <DescribedTerm description={description}>{symbol}</DescribedTerm>
      </Symbol>
      <Value>{value}</Value>
      {sourceContent}
    </Row>
  )
}

/**
 * Convenience: extract a `FieldValueSource | null` from the raw `FieldValue`
 * wrapper persisted on `ticket.fieldValues`. Mirrors what `ConditionalFormSection`
 * does to render its citation chip.
 */
export const fieldSourceFromTicket = (
  fieldValue: unknown
): FieldValueSource | null => {
  return getFieldSource(fieldValue as Parameters<typeof getFieldSource>[0])
}
